// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The tilt bob (PH-072, PH-A27).
//
// A real tilt bob is a plumb hanging inside a metal ring. Nudge the machine and the plumb
// swings; if it touches the ring, the machine notices. It is simulated as exactly that —
// a damped 2-D pendulum driven by the machine frame's acceleration — rather than as a
// counter of how hard someone pressed a key, and the difference is the whole feel of
// nudging.
//
// **Why the pendulum matters and a threshold would not.** A plumb on a 150 mm wire swings at
// about 1.3 Hz and keeps ringing for seconds. So a second nudge landing while the bob is
// already swinging *towards* the ring tilts a machine that would have tolerated either nudge
// alone, and a second nudge timed against the swing is nearly free. Skilled players feel
// that rhythm and play to it. A "nudge strength" threshold cannot produce it at any amount
// of tuning, because the information it needs — where the bob is right now — does not exist.
//
// **It is deliberately asymmetric.** The ring is an ellipse with more clearance front-to-back
// than side-to-side, which is why lifting the front of a real machine is more forgiving than
// slapping its side (PH-A27).

import { G, STEP_HZ } from '../sim/constants'
import { sqrt } from '../math/deterministic'

/** Plumb wire length, metres. Sets the swing period: `ω = √(g/L)` ≈ 8.09 rad/s, about 1.3 Hz. */
export const BOB_LENGTH = 0.15

/**
 * Ring clearance, metres. Lateral is the tighter axis — a side slap is the easy way to tilt.
 *
 * **Measured against the frame model rather than guessed, and the guess was badly wrong.**
 * The first attempt used 8 mm and 13 mm, on the reasoning that a nudge shifts the cabinet
 * about a centimetre. It does — but the cabinet frame rings at ω ≈ 30 rad/s while the bob
 * rings at 8.09, so the machine moves out and back in about a tenth of a second and the slow
 * pendulum only picks up a fraction of it. A full 26 N·s nudge displaces the bob by **3.03
 * mm**, not ten, and nothing would ever have tilted.
 *
 * Calibrated from the measured response, at 1000 Hz against `World`'s frame spring:
 *
 * | nudges | best spacing | peak |
 * |---|---|---|
 * | 1 | — | 3.03 mm |
 * | 2 | 600 steps | 4.29 mm |
 * | 3 | 600 steps | 4.54 mm |
 * | 5 | 700 steps | 5.07 mm |
 *
 * So 3.6 mm lateral means one nudge is always safe, two well-timed ones reach the ring, and
 * two badly timed ones (389 steps apart, against the swing — 3.11 mm) do not. 4.6 mm
 * front-to-back needs four or five. That gap is PH-A27's asymmetry, and it lands where a real
 * bob's clearance actually is: a plumb inside a ring has millimetres of play, not a centimetre.
 *
 * **[VERIFY]** against a real machine. These are calibrated to this frame model, which is
 * itself a calibration; what is defensible is the behaviour, not the millimetre.
 */
export const RING_RADIUS_X = 0.0036
/** Front-to-back ring clearance. Wider on purpose (PH-A27). */
export const RING_RADIUS_Y = 0.0046

/**
 * Damping ratio. Light, so the bob rings for a few seconds and a second nudge lands on a
 * moving pendulum rather than a settled one.
 */
export const BOB_DAMPING = 0.08

/** Warnings expire this long after they are given (PH-072). */
export const WARNING_WINDOW_SECONDS = 10

/**
 * Contacts inside the window that end the ball. Two warnings, then the third bites.
 *
 * **The default, not the rule.** 04 §15 gives the operator `tiltWarnings`, default 2 — and this
 * constant is `warnings + 1`, which is the same number said the other way round: the third contact
 * is the one that is not a warning. The two agreed for four milestones because somebody typed them
 * both and nothing held them to each other; `Game` now sets `contactsToTilt` from the machine and
 * this is what a machine that declares nothing gets.
 */
export const CONTACTS_TO_TILT = 3

/**
 * Frame speed above which the machine is being assaulted rather than nudged, m/s.
 *
 * A separate, much higher threshold than the bob, because slam tilt is a different switch on
 * a real machine — a leaf switch on the coin door that a hard shove closes — and it ends the
 * whole game rather than the ball.
 */
export const SLAM_FRAME_SPEED = 0.55

export type TiltEvent = 'none' | 'warning' | 'tilt' | 'slam'

export class TiltBob {
  /**
   * Contacts inside the window that end the ball (04 §15's `tiltWarnings`, plus one).
   *
   * A field rather than the constant it defaults to, because the operator sets it. Zero warnings
   * is a legal setting — §15's range is `[0, 4]` — and it means the *first* contact tilts, which
   * is a real and famously unpleasant machine to play; `Math.max(1, …)` in `Game` is what keeps it
   * from becoming a bob that can never tilt at all.
   */
  contactsToTilt = CONTACTS_TO_TILT

  /** Bob displacement from the ring centre, metres. */
  x = 0
  y = 0
  vx = 0
  vy = 0

  /** Steps at which each unexpired contact happened. */
  private readonly contacts: number[] = []
  /** True while the bob is touching the ring, so one swing counts once. */
  private touching = false

  private prevFrameVX = 0
  private prevFrameVY = 0

  /**
   * The step of the most recent ring contact, or −1 (M3-26).
   *
   * Stamped for **every** touch, including the ones that score nothing — after the ball is
   * already tilted, and after the third contact has bitten. `advance` returns an event only when
   * a contact *counts*, which is the right answer for the rules and the wrong one for the ear:
   * 07 §3.6 asks for "a distinct contact ting on each ring touch", and a bob that stops ringing
   * once the ball is dead is a bob that has stopped being a physical object.
   *
   * The same arrangement as `Flipper.pressedAtStep`. Presentation-only; nothing here reads it.
   */
  contactedAtStep = -1

  /** Latched for the rest of the ball. */
  tilted = false
  /** Latched for the rest of the game. */
  slammed = false

  warnings = 0
  step = 0

  /**
   * The bob's whole state (M4-28a).
   *
   * `touching`, `prevFrameVX` and `prevFrameVY` are private and are exactly the fields a
   * hand-written snapshot forgets: they are how the bob knows it is *already* against the ring,
   * so a game restored without them registers a fresh contact on the next step and can tilt a
   * player who did nothing.
   *
   * **`contacts` was forgotten in exactly that way, and it forgave players their warnings.**
   * `warnings` is a *count* and `contacts` is the list it is counted from — so a restore that
   * carried the number and not the list produced a bob that said "two warnings" and, on its next
   * ring contact, expired an empty list and went back to one. Measured on NEON NOVA with a shove
   * every 700 steps: the uninterrupted game tilted 137 steps after the boundary and the restored
   * one took 2,756. A chunked validation of a game that tilted would not tilt, score differently,
   * and tell the player `mismatch`.
   */
  snapshot(): TiltBobState {
    return {
      contacts: [...this.contacts],
      x: this.x,
      y: this.y,
      vx: this.vx,
      vy: this.vy,
      touching: this.touching,
      prevFrameVX: this.prevFrameVX,
      prevFrameVY: this.prevFrameVY,
      contactedAtStep: this.contactedAtStep,
      tilted: this.tilted,
      slammed: this.slammed,
      warnings: this.warnings,
      step: this.step,
    }
  }

  restore(s: TiltBobState): void {
    this.contacts.length = 0
    for (const step of s.contacts) this.contacts.push(step)
    this.x = s.x
    this.y = s.y
    this.vx = s.vx
    this.vy = s.vy
    this.touching = s.touching
    this.prevFrameVX = s.prevFrameVX
    this.prevFrameVY = s.prevFrameVY
    this.contactedAtStep = s.contactedAtStep
    this.tilted = s.tilted
    this.slammed = s.slammed
    this.warnings = s.warnings
    this.step = s.step
  }

  private readonly omega: number
  private readonly windowSteps: number

  constructor() {
    this.omega = sqrt(G / BOB_LENGTH)
    this.windowSteps = WARNING_WINDOW_SECONDS * STEP_HZ
  }

  /** New ball: the bob keeps swinging but the penalty and the warnings are cleared. */
  resetBall(): void {
    this.tilted = false
    this.warnings = 0
    this.contacts.length = 0
    this.touching = false
  }

  resetGame(): void {
    this.resetBall()
    this.slammed = false
    this.contactedAtStep = -1
    this.x = 0
    this.y = 0
    this.vx = 0
    this.vy = 0
    this.prevFrameVX = 0
    this.prevFrameVY = 0
  }

  /**
   * Advance one simulation step, driven by the machine frame's velocity.
   *
   * The bob feels the frame's **acceleration**, which is what a pendulum responds to — both
   * the kick of the nudge and the spring pulling the cabinet back. Passing velocity and
   * differencing here keeps the caller from having to know that.
   */
  advance(stepIndex: number, frameVX: number, frameVY: number, dt: number): TiltEvent {
    this.step = stepIndex

    const ax = (frameVX - this.prevFrameVX) / dt
    const ay = (frameVY - this.prevFrameVY) / dt
    this.prevFrameVX = frameVX
    this.prevFrameVY = frameVY

    // Damped pendulum in the machine frame. The frame's acceleration appears as a pseudo-
    // force on the bob, which is exactly why the bob measures the shove rather than the ball.
    const k = this.omega * this.omega
    const c = 2 * BOB_DAMPING * this.omega
    const accX = -k * this.x - c * this.vx - ax
    const accY = -k * this.y - c * this.vy - ay
    this.vx += accX * dt
    this.vy += accY * dt
    this.x += this.vx * dt
    this.y += this.vy * dt

    // Slam tilt is judged on the frame itself, not the bob: it is a different switch.
    const frameSpeed = sqrt(frameVX * frameVX + frameVY * frameVY)
    if (!this.slammed && frameSpeed > SLAM_FRAME_SPEED) {
      this.slammed = true
      return 'slam'
    }

    const nx = this.x / RING_RADIUS_X
    const ny = this.y / RING_RADIUS_Y
    const reach = nx * nx + ny * ny

    if (reach < 1) {
      this.touching = false
      return 'none'
    }
    if (this.touching) return 'none'
    this.touching = true
    this.contactedAtStep = stepIndex

    // The bob cannot leave the ring, so it bounces off it. Without this the bob would sail
    // out to an impossible displacement and stay in contact for the whole swing.
    const scale = 1 / sqrt(reach)
    this.x *= scale
    this.y *= scale
    this.vx *= -0.35
    this.vy *= -0.35

    if (this.tilted) return 'none'

    this.expire()
    this.contacts.push(stepIndex)

    if (this.contacts.length >= this.contactsToTilt) {
      this.tilted = true
      return 'tilt'
    }
    this.warnings = this.contacts.length
    return 'warning'
  }

  /** Warnings older than the window no longer count towards a tilt. */
  private expire(): void {
    const cutoff = this.step - this.windowSteps
    let keep = 0
    for (let i = 0; i < this.contacts.length; i++) {
      if (this.contacts[i]! > cutoff) {
        this.contacts[keep] = this.contacts[i]!
        keep++
      }
    }
    this.contacts.length = keep
    this.warnings = keep
  }

  /** How close the bob is to the ring, 0 at rest and 1 at contact. For a diagnostic display. */
  get proximity(): number {
    const nx = this.x / RING_RADIUS_X
    const ny = this.y / RING_RADIUS_Y
    return sqrt(nx * nx + ny * ny)
  }
}

/** The tilt bob's mutable state (M4-28a). */
export interface TiltBobState {
  /** Unexpired contact steps. The list `warnings` is a count of, not a duplicate of it. */
  readonly contacts: readonly number[]
  readonly x: number
  readonly y: number
  readonly vx: number
  readonly vy: number
  readonly touching: boolean
  readonly prevFrameVX: number
  readonly prevFrameVY: number
  readonly contactedAtStep: number
  readonly tilted: boolean
  readonly slammed: boolean
  readonly warnings: number
  readonly step: number
}
